import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Select from 'react-select';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { APIintegrationCallMethod } from '../../../api/Integrations';

import ProcessingSpinner from '../../ProcessingSpinner';
import ErrorBoundary from '../../ErrorBoundary';

const DesignPickleRequestModal = ({
  handleClose,
  text,
  files,
}) => {
  const [request, setRequest] = useState({
    name: '',
    directions: '',
    success: false,
  });
  const [requestType, setRequestType] = useState(null);
  const [requestTypes, setRequestTypes] = useState([]);
  const [requestTypesLoading, setRequestTypesLoading] = useState(true);
  const [requestFormats, setRequestFormats] = useState([]);
  const [requestSizes, setRequestSizes] = useState([]);
  const [dpUrl, setDpUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [invalidFields, setInvalidFields] = useState([]);

  const intl = useIntl();

  useEffect(() => {
    setRequestTypesLoading(true);
    APIintegrationCallMethod('design-pickle', 'getRequestTypes').then((response) => {
      setRequestTypes(response);
      setRequestTypesLoading(false);
    });
  }, []);

  const getRequestTypesOptions = () => {
    const groups = [];
    requestTypes.forEach((rt) => {
      if (!groups[rt.category]) {
        groups[rt.category] = [];
      }
      groups[rt.category].push(rt);
    });

    const options = [];
    Object.keys(groups).forEach((key) => {
      options.push({
        label: key,
        options: groups[key],
      });
    });
    return options;
  };

  const getRequestFormatsOptions = () => {
    const options = [];
    requestType.formats.forEach((format) => {
      options.push({
        id: format.id,
        label: format.name,
      });
    });
    return options;
  };

  const getRequestSizesOptions = () => {
    const options = [];
    requestType.sizes.forEach((size) => {
      options.push({
        id: size.id,
        label: size.details,
      });
    });
    return options;
  };

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setRequest({
      ...request,
      [name]: value,
    });
  };

  const validateFields = () => {
    let valid = true;

    setInvalidFields([]);

    if (!requestType) {
      setInvalidFields((prevState) => ([
        ...prevState,
        'request-type',
      ]));
      valid = false;
    } else {
      if (!request.name) {
        setInvalidFields((prevState) => ([
          ...prevState,
          'name',
        ]));
        valid = false;
      }
      if (!request.directions) {
        setInvalidFields((prevState) => ([
          ...prevState,
          'directions',
        ]));
        valid = false;
      }
      if (requestFormats.length === 0) {
        setInvalidFields((prevState) => ([
          ...prevState,
          'request-formats',
        ]));
        valid = false;
      }
      if (requestSizes.length === 0) {
        setInvalidFields((prevState) => ([
          ...prevState,
          'request-sizes',
        ]));
        valid = false;
      }
    }

    setInvalidFields((prevState) => ([...window._.uniq(prevState)]));

    return valid;
  };

  const handleCreateRequest = () => {
    setError(false);
    setErrorMessage('');

    if (!validateFields()) {
      return;
    }

    setProcessing(true);

    const params = {
      request_type_id: requestType?.id,
      name: request.name,
      directions: request.directions.split(/\r?\n|\r|\n/g),
      formats: requestFormats.map(f => f.label.toLowerCase()),
      sizes: requestSizes.map(s => s.label.replaceAll(' ', '')),
      files: files.map(f => f.uuid),
    };
    APIintegrationCallMethod('design-pickle', 'createDesignRequest', params)
      .then((response) => {
        setProcessing(false);
        if (response.data.url) {
          setDpUrl(response.data.url);
        }
      })
      .catch(() => {
        setError(true);
      });
  };

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose}>
        {intl.formatMessage({ defaultMessage: 'Create a new request in Design Pickle' })}
      </ModalHeader>
      <ModalBody>
        {dpUrl ? (
          <div className="center">
            <p>
              <strong>
                {intl.formatMessage({ defaultMessage: 'Your design request has been created!' })}
              </strong>
            </p>
            <p>
              <Button tag="a" href={dpUrl} target="_blank" rel="noreferrer">
                {intl.formatMessage({ defaultMessage: 'View in Design Pickle' })}
              </Button>
            </p>
          </div>
        ) : (
          <>
            <p className="center">
              {text}
            </p>
            <Form>
              <FormGroup className="dp-request-type">
                <Label for="request-type">
                  {intl.formatMessage({ defaultMessage: 'Request Type' })}
                  <span className="required-asterisk">*</span>
                </Label>
                <Select
                  id="request-type"
                  className={invalidFields.includes('request-type') ? 'select-is-invalid' : ''}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  menuPortalTarget={document.body}
                  isSearchable={true}
                  isDisabled={requestTypesLoading}
                  isLoading={requestTypesLoading}
                  placeholder={
                    requestTypesLoading ?
                      intl.formatMessage({ defaultMessage: 'Loading...' }) :
                      intl.formatMessage({ defaultMessage: 'Select a request type' })
                  }
                  onChange={(v) => { setRequestType(v); }}
                  value={requestType}
                  options={getRequestTypesOptions()}
                  getOptionValue={option => option.id}
                  getOptionLabel={option => option.name}
                />
                {invalidFields.includes('request-type') && (
                  <div className="invalid-feedback" style={{ display: 'block' }}>
                    {intl.formatMessage({ defaultMessage: 'This field is required' })}
                  </div>
                )}
              </FormGroup>
              {requestType && (
                <>
                  <FormGroup>
                    <Label for="request-formats">
                      {intl.formatMessage({ defaultMessage: 'File Formats' })}
                      <span className="required-asterisk">*</span>
                    </Label>
                    <Select
                      id="request-formats"
                      className={invalidFields.includes('request-formats') ? 'select-is-invalid' : ''}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                      menuPortalTarget={document.body}
                      isSearchable={true}
                      isMulti
                      isDisabled={requestTypesLoading}
                      isLoading={requestTypesLoading}
                      placeholder={intl.formatMessage({ defaultMessage: 'Select file formats' })}
                      onChange={(v) => { setRequestFormats(v); }}
                      value={requestFormats}
                      options={getRequestFormatsOptions()}
                      getOptionValue={option => option.id}
                    />
                    {invalidFields.includes('request-formats') && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {intl.formatMessage({ defaultMessage: 'This field is required' })}
                      </div>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <Label for="request-sizes">
                      {intl.formatMessage({ defaultMessage: 'File Sizes' })}
                      <span className="required-asterisk">*</span>
                    </Label>
                    <Select
                      id="request-sizes"
                      className={invalidFields.includes('request-sizes') ? 'select-is-invalid' : ''}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                      menuPortalTarget={document.body}
                      isSearchable={true}
                      isMulti
                      isDisabled={requestTypesLoading}
                      isLoading={requestTypesLoading}
                      placeholder={intl.formatMessage({ defaultMessage: 'Select file sizes' })}
                      onChange={(v) => { setRequestSizes(v); }}
                      value={requestSizes}
                      options={getRequestSizesOptions()}
                      getOptionValue={option => option.id}
                    />
                    {invalidFields.includes('request-sizes') && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {intl.formatMessage({ defaultMessage: 'This field is required' })}
                      </div>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <Label for="request-name">
                      {intl.formatMessage({ defaultMessage: 'Request name' })}
                      <span className="required-asterisk">*</span>
                    </Label>
                    <Input type="text" required id="request-name" name="name" onChange={handleChange} invalid={invalidFields.includes('name')} />
                    <FormFeedback>{intl.formatMessage({ defaultMessage: 'This field is required' })}</FormFeedback>
                  </FormGroup>
                  <FormGroup>
                    <Label for="request-directions">
                      {intl.formatMessage({ defaultMessage: 'Directions' })}
                      <span className="required-asterisk">*</span>
                    </Label>
                    <Input type="textarea" required id="request-directions" name="directions" onChange={handleChange} invalid={invalidFields.includes('directions')} />
                    <FormFeedback>{intl.formatMessage({ defaultMessage: 'This field is required' })}</FormFeedback>
                  </FormGroup>
                </>
              )}
            </Form>
            {(error || errorMessage) && (
              <p className="error">
                {errorMessage ? (
                  `${errorMessage}`
                ) : (
                  intl.formatMessage({ defaultMessage: 'Something went wrong. Please try again or contact us.' })
                )}
              </p>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="buttons-group">
          {dpUrl ? (
            <Button
              color="secondary"
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Close' })}
            </Button>
          ) : (
            <>
              {!processing && (
                <Button
                  color="secondary"
                  onClick={handleClose}
                >
                  {intl.formatMessage({ defaultMessage: 'Cancel' })}
                </Button>
              )}
              <Button
                className="confirmation-button"
                color="primary"
                disabled={requestTypesLoading || processing || !!errorMessage}
                onClick={handleCreateRequest}
              >
                <ProcessingSpinner show={processing} />
                {processing ? (
                  intl.formatMessage({ defaultMessage: 'Creating...' })
                ) : (
                  intl.formatMessage({ defaultMessage: 'Create' })
                )}
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

DesignPickleRequestModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  files: PropTypes.array.isRequired,
};

export default connect()(DesignPickleRequestModal);
